import React from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { EVENT_TYPE } from 'src/features/eventLog/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { actionMessages } from 'src/features/navigation/utils/messages';
import { useGetPricingPlanChangeOptions } from 'src/features/stay/api/useGetPricingPlanChangeOptions';
import ChangeActiveStayPricingPlanFormModal from 'src/features/stay/components/forms/ChangeActiveStayPricingPlanFormModal';
import ConvertActiveStayToMembershipModal from 'src/features/stay/components/forms/ConvertActiveStayToMembershipModal';
import ExitFromParkingFormModal from 'src/features/stay/components/forms/ExitFromParkingFormModal';
import { ActiveStay, PERIOD_VISITOR_CATEGORY, STAY_TYPE } from 'src/features/stay/types';
import CreateVisitorModalForm from 'src/features/visitor/components/form/CreateVisitorModalForm';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { Button } from 'src/registry/ui/Button';
import DropdownButtonGroup from 'src/registry/ui/DropdownButtonGroup';

type ActiveStaySidePanelButtonsProps = {
    stayId?: string;
    stayType: STAY_TYPE;
    stay?: ActiveStay;
    eventType?: EVENT_TYPE;
};

const ActiveStaySidePanelButtons = ({ stayId = '', stayType, stay, eventType }: ActiveStaySidePanelButtonsProps) => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const { parkingId } = useParking();
    const manageActiveStays = useHasPermissions([PERMISSION.MANAGE_ACTIVE_STAYS]);
    const manageMembership = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { data: pricingPlanChangeOptions } = useGetPricingPlanChangeOptions({
        stayId,
        parkingId,
        config: { enabled: stayType === STAY_TYPE.ACTIVE },
    });

    const activePastStayUrlPart = stayType === 'active' ? 'active-stay' : 'past-stay';
    const isActiveStayClient = Boolean(
        stayType === STAY_TYPE.ACTIVE && stay?.period_visitor_category === PERIOD_VISITOR_CATEGORY.CLIENT,
    );

    const showChangePricingPlanBtn = pricingPlanChangeOptions?.can_be_changed && manageActiveStays;
    const showConvertToMembershipBtn = isActiveStayClient && manageMembership && manageActiveStays;
    const showExitFromParkingBtn = stayType === STAY_TYPE.ACTIVE && stay && manageActiveStays;
    const showCreateVisitor =
        manageActiveStays &&
        (eventType === EVENT_TYPE.LPR_CAMERA_DETECTION || eventType === EVENT_TYPE.GATEWAY_PASS_NOT_ALLOWED);

    const numberOfAdditionalButtons =
        Number(showConvertToMembershipBtn) +
        Number(showExitFromParkingBtn) +
        Number(showCreateVisitor) +
        Number(showChangePricingPlanBtn);

    return (
        <>
            {showCreateVisitor && <CreateVisitorModalForm open={isModalOpen} onOpenChange={setIsModalOpen} />}
            <DropdownButtonGroup
                dropdownButtonTitle={formatMessage(visitorMessages.numberOfHiddenButtons, {
                    count: numberOfAdditionalButtons,
                })}
                isLoading={stay === undefined && showExitFromParkingBtn === undefined}
            >
                <Button
                    variant="default"
                    className="cursor-pointer"
                    onClick={() =>
                        stayId &&
                        navigate(`/${PATH.VISITORS}/${activePastStayUrlPart}/dashboard/${stayId}?type=${stayType}`)
                    }
                >
                    {formatMessage(visitorMessages.openDashboard)}
                </Button>
                {showConvertToMembershipBtn && (
                    <ConvertActiveStayToMembershipModal selectedActiveStay={stay}>
                        <Button className="cursor-pointer" variant="outline">
                            {formatMessage(visitorMessages.convertActiveStay)}
                        </Button>
                    </ConvertActiveStayToMembershipModal>
                )}
                {showChangePricingPlanBtn && (
                    <ChangeActiveStayPricingPlanFormModal
                        stayId={stayId}
                        pricingPlanChangeOptions={pricingPlanChangeOptions}
                    >
                        <Button className="cursor-pointer" variant="outline" onClick={() => setIsModalOpen(true)}>
                            {formatMessage(visitorMessages.changePricingPlan)}
                        </Button>
                    </ChangeActiveStayPricingPlanFormModal>
                )}
                {showCreateVisitor && (
                    <Button className="cursor-pointer" variant="outline" onClick={() => setIsModalOpen(true)}>
                        {formatMessage(actionMessages.addVisitor)}
                    </Button>
                )}
                {showExitFromParkingBtn && (
                    <ExitFromParkingFormModal activeStay={stay}>
                        <Button className="cursor-pointer" variant="destructiveLight">
                            {formatMessage(visitorMessages.exitFromParking)}
                        </Button>
                    </ExitFromParkingFormModal>
                )}
            </DropdownButtonGroup>
        </>
    );
};

export default ActiveStaySidePanelButtons;
